import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";

interface ImportQuestion {
  question_text: string;
  question_type: "mcq" | "open_ended";
  options?: Record<string, string>;
  correct_answer?: string;
  score: number;
  is_must_have: boolean;
  ai_evaluation_criteria?: string | null;
  difficulty: string;
  is_enabled: boolean;
}

interface ImportQuestionsDialogProps {
  open: boolean;
  onClose: () => void;
  selectedGroupId: string;
  onSuccess: () => void;
}

const EXAMPLE_JSON = `[
  {
    "question_text": "What is the time complexity of binary search?",
    "question_type": "mcq",
    "options": {
      "A": "O(n)",
      "B": "O(log n)",
      "C": "O(n²)",
      "D": "O(1)"
    },
    "correct_answer": "B",
    "score": 10,
    "is_must_have": false,
    "ai_evaluation_criteria": null,
    "difficulty": "medium",
    "is_enabled": true
  },
  {
    "question_text": "Explain the concept of closures in JavaScript and provide an example.",
    "question_type": "open_ended",
    "score": 15,
    "is_must_have": true,
    "ai_evaluation_criteria": "Look for understanding of scope, variable access, and practical examples. Award full points for clear explanation with working code example.",
    "difficulty": "hard",
    "is_enabled": true
  }
]`;

export const ImportQuestionsDialog = ({
  open,
  onClose,
  selectedGroupId,
  onSuccess,
}: ImportQuestionsDialogProps) => {
  const [jsonData, setJsonData] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { toast } = useToast();

  const validateQuestion = (question: any, index: number): string[] => {
    const errors: string[] = [];
    const prefix = `Question ${index + 1}:`;

    if (!question.question_text || typeof question.question_text !== "string") {
      errors.push(`${prefix} question_text is required and must be a string`);
    }

    if (!["mcq", "open_ended"].includes(question.question_type)) {
      errors.push(
        `${prefix} question_type must be either "mcq" or "open_ended"`
      );
    }

    if (question.question_type === "mcq") {
      if (!question.options || typeof question.options !== "object") {
        errors.push(`${prefix} MCQ questions must have options object`);
      } else {
        // Validate option keys are uppercase letters
        const optionKeys = Object.keys(question.options);
        const validKeys = optionKeys.filter((key) => /^[A-Z]$/.test(key));
        if (validKeys.length !== optionKeys.length) {
          errors.push(
            `${prefix} Option keys must be uppercase letters (A, B, C, D, etc.)`
          );
        }
      }
      if (
        !question.correct_answer ||
        typeof question.correct_answer !== "string"
      ) {
        errors.push(`${prefix} MCQ questions must have correct_answer`);
      } else {
        // Validate correct_answer is an uppercase letter
        if (!/^[A-Z]$/.test(question.correct_answer)) {
          errors.push(
            `${prefix} correct_answer must be an uppercase letter (A, B, C, D, etc.)`
          );
        }
      }
    }

    if (typeof question.score !== "number" || question.score <= 0) {
      errors.push(`${prefix} score must be a positive number`);
    }

    if (typeof question.is_must_have !== "boolean") {
      errors.push(`${prefix} is_must_have must be a boolean`);
    }

    if (!question.difficulty || typeof question.difficulty !== "string") {
      errors.push(`${prefix} difficulty is required and must be a string`);
    }

    if (typeof question.is_enabled !== "boolean") {
      errors.push(`${prefix} is_enabled must be a boolean`);
    }

    return errors;
  };

  const handleImport = async () => {
    setIsLoading(true);
    setValidationErrors([]);

    try {
      // Parse JSON
      let parsedData: ImportQuestion[];
      try {
        parsedData = JSON.parse(jsonData);
      } catch (error) {
        setValidationErrors(["Invalid JSON format. Please check your syntax."]);
        setIsLoading(false);
        return;
      }

      // Validate that it's an array
      if (!Array.isArray(parsedData)) {
        setValidationErrors(["JSON data must be an array of questions."]);
        setIsLoading(false);
        return;
      }

      if (parsedData.length === 0) {
        setValidationErrors(["Array cannot be empty."]);
        setIsLoading(false);
        return;
      }

      // Validate each question
      const allErrors: string[] = [];
      parsedData.forEach((question, index) => {
        const questionErrors = validateQuestion(question, index);
        allErrors.push(...questionErrors);
      });

      if (allErrors.length > 0) {
        setValidationErrors(allErrors);
        setIsLoading(false);
        return;
      }

      // Transform data for database insertion
      const questionsToInsert = parsedData.map((question) => ({
        question_text: question.question_text,
        question_type: question.question_type,
        options: question.options || null,
        correct_answer: question.correct_answer || null,
        score: question.score,
        is_must_have: question.is_must_have,
        ai_evaluation_criteria: question.ai_evaluation_criteria || null,
        difficulty: question.difficulty,
        is_enabled: question.is_enabled,
        group_id: selectedGroupId,
        job_position_ids: [], // Empty array as default
      }));

      // Insert into database
      const { error } = await supabase
        .from("questions")
        .insert(questionsToInsert);

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: `Successfully imported ${questionsToInsert.length} questions`,
      });

      onSuccess();
      onClose();
      setJsonData("");
      setValidationErrors([]);
    } catch (error) {
      console.error("Import error:", error);
      toast({
        title: "Error",
        description: "Failed to import questions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setJsonData("");
    setValidationErrors([]);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Questions from JSON</DialogTitle>
          <DialogDescription>
            Paste your JSON array of questions below. Each question should
            follow the required format.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="json-data">JSON Data</Label>
            <Textarea
              id="json-data"
              placeholder={EXAMPLE_JSON}
              value={jsonData}
              onChange={(e) => setJsonData(e.target.value)}
              className="min-h-[300px] font-mono text-sm"
            />
          </div>

          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-medium text-red-800 mb-2">
                Validation Errors:
              </h4>
              <ul className="text-sm text-red-700 space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index} className="list-disc list-inside">
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">Required Format:</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>
                • <code>question_text</code>: String - The question content
              </li>
              <li>
                • <code>question_type</code>: "mcq" or "open_ended"
              </li>
              <li>
                • <code>options</code>: Object - Required for MCQ questions with
                uppercase keys (e.g., {`{"A": "Option A", "B": "Option B"}`})
              </li>
              <li>
                • <code>correct_answer</code>: String - Required for MCQ
                questions, must be uppercase letter (e.g., "A")
              </li>
              <li>
                • <code>score</code>: Number - Points for this question
              </li>
              <li>
                • <code>is_must_have</code>: Boolean - Whether this is a
                required question
              </li>
              <li>
                • <code>ai_evaluation_criteria</code>: String or null - For
                open-ended questions
              </li>
              <li>
                • <code>difficulty</code>: String - Difficulty level
              </li>
              <li>
                • <code>is_enabled</code>: Boolean - Whether question is active
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={isLoading || !jsonData.trim()}
          >
            {isLoading ? (
              <>
                <LoadingSpinner />
                Importing...
              </>
            ) : (
              "Import Questions"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
